import React, { useState } from 'react';
import './PdfLibrary.css';
import dashboardService from "../../services/dashboardService"

function PdfLibrary({ pdfs, loading, onPdfDelete, onViewPdf, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  if (loading) {
    return (
      <div className="library-loading">
        <div className="loading-spinner"></div>
        <p>Loading your PDF library...</p>
      </div>
    );
  }

  // Filter PDFs based on search and category
  const filteredPdfs = pdfs.filter(pdf => {
    const matchesSearch = pdf.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pdf.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           pdf.documentCategory?.id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort PDFs
  const sortedPdfs = [...filteredPdfs].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.uploadedAt) - new Date(a.uploadedAt);
      case 'oldest':
        return new Date(a.uploadedAt) - new Date(b.uploadedAt);
      case 'name':
        return a.name?.localeCompare(b.name);
      case 'size':
        return b.size - a.size;
      default:
        return 0;
    }
  });

  // Get unique categories for filter
  const categories = [...new Set(pdfs.map(pdf => pdf.documentCategory?.id).filter(Boolean))];

  const handleDelete = async (pdfId, pdfName) => {
    if (window.confirm(`Are you sure you want to delete "${pdfName}"?`)) {
      try {
        await dashboardService.deletePdf(pdfId);
        onPdfDelete();
        alert('PDF deleted successfully!');
      } catch (error) {
        console.error('❌ Failed to delete PDF:', error);
        alert('Failed to delete PDF. Please try again.');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="pdf-library">
      {/* Library Header - Sticky */}
      <div className="library-header">
        <div className="library-stats">
          <h3>My Documents ({pdfs.length})</h3>
          <p>Manage and view your uploaded PDFs</p>
        </div>
        <button onClick={onRefresh} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {/* Search and Filter - Sticky */}
      <div className="library-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-filter"
        >
          <option value="all">All Categories</option>
          {categories.map(categoryId => {
            const category = pdfs.find(pdf => pdf.documentCategory?.id === categoryId)?.documentCategory;
            return (
              <option key={categoryId} value={categoryId}>
                {category?.name || categoryId}
              </option>
            );
          })}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="sort-filter"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="name">Name A-Z</option>
          <option value="size">Size</option>
        </select>
      </div>

      {/* Scrollable PDF Grid Container */}
      <div className="pdf-grid-container">
        {sortedPdfs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3>No documents found</h3>
            <p>
              {pdfs.length === 0 
                ? ""
                : "No documents match your search criteria."
              }
            </p>
          </div>
        ) : (
          <div className="pdf-grid">
            {sortedPdfs.map(pdf => (
              <div key={pdf.id} className="pdf-card">
                <div className="pdf-card-header">
                  <div 
                    className="category-badge"
                    style={{ 
                      backgroundColor: pdf.documentCategory?.color || '#6B7280',
                      color: 'white'
                    }}
                  >
                    {pdf.documentCategory?.name || 'General'}
                  </div>
                  <div className="card-actions">
                    <button 
                      onClick={() => onViewPdf(pdf.id, pdf.name)}
                      className="action-button view-button"
                      title="View PDF"
                    >
                      START▶️
                    </button>
                    <button 
                      onClick={() => handleDelete(pdf.id, pdf.name)}
                      className="action-button delete-button"
                      title="Delete PDF"
                    >

                    </button>
                  </div>
                </div>

                <div className="pdf-card-body">
                  <h4 className="pdf-title">{pdf.name}</h4>
                  {pdf.description && (
                    <p className="pdf-description">{pdf.description}</p>
                  )}
                  
                  {pdf.summary && pdf.summary !== 'Generating summary...' && (
                    <div className="pdf-summary">
                      <strong>Summary:</strong> 
                      <p>{pdf.summary.length > 150 ? `${pdf.summary.substring(0, 150)}...` : pdf.summary}</p>
                    </div>
                  )}
                </div>

                <div className="pdf-card-footer">
                  <div className="pdf-meta">
                    <div className="meta-item">
                      <span className="meta-label">Pages:</span>
                      <span className="meta-value">{pdf.contentMetadata?.pageCount || 'N/A'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Size:</span>
                      <span className="meta-value">{formatFileSize(pdf.size)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Uploaded:</span>
                      <span className="meta-value">{formatDate(pdf.uploadedAt)}</span>
                    </div>
                  </div>
                  
                  <div className="pdf-stats">
                    <div className="stat">
                      <span className="stat-icon">🖍️</span>
                      <span>{pdf.highlights?.length || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-icon">💬</span>
                      <span>{pdf.comments?.length || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-icon">📝</span>
                      <span>{pdf.notes?.content ? 1 : 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfLibrary;